import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

interface FieldHelpPopoverProps {
  triggerLabel: string;
  popoverLabel: string;
  title: string;
  children: ReactNode;
}

export default function FieldHelpPopover({ triggerLabel, popoverLabel, title, children }: FieldHelpPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="field-help" ref={containerRef}>
      <button
        type="button"
        className="field-help-trigger"
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={popoverId}
        onClick={() => setIsOpen((current) => !current)}
      >
        ?
      </button>
      {isOpen && (
        <div id={popoverId} role="dialog" aria-modal="false" aria-label={popoverLabel} className="field-help-popover">
          <span className="field-help-title">{title}</span>
          {children}
        </div>
      )}
    </div>
  );
}